import { IconButton, Stack, Typography, Icon, Box, InputAdornment, Tooltip, SxProps, Theme } from '@mui/material';
import { forwardRef, useCallback, useLayoutEffect, useRef, useState, useImperativeHandle, memo } from 'react';

import { Store } from 'app/model/cris/store';

import { dialogService } from 'app/services/dialog.service';

import { ButtonTruncated } from './button-truncated';
import { StorePicker } from './store-picker';
export interface MlTrainingActionsBarType {
    initialSelectionCount?: number;
    primaryStore?: Store;
    onApplyActions: (store: Store | undefined, positive: boolean) => void;
    onClearMark: () => void;
    onClear: () => void;
    sx?: SxProps<Theme>;
}

const inputPaddingsWidth = 86;

export interface SelectionCountHandle {
    setSelectionCount: (newCount: number) => void;
}
const MlTrainingActionsBar = forwardRef<SelectionCountHandle, MlTrainingActionsBarType>(
    ({ initialSelectionCount = 0, primaryStore, onApplyActions, onClearMark, onClear, sx }, ref) => {
        const [selectionCount, setSelectionCount] = useState(initialSelectionCount);
        useImperativeHandle(ref, () => ({
            setSelectionCount,
        }));
        const [storePickerStore, setStorePickerStore] = useState<Store | undefined>();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, no-null/no-null
        const pickerRef = useRef<HTMLDivElement>(null!);
        const [dynamicWidth, setDynamicWidth] = useState(0);
        const [disableInputTooltip, setDisableInputTooltip] = useState(true);
        const [inputMenuIsOpen, setInputMenuIsOpen] = useState(false);
        const setWidth = useCallback(() => {
            const newDynamicWidth =
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                pickerRef.current.querySelector('#input_hidden_placeholder')!.scrollWidth + inputPaddingsWidth;
            setDynamicWidth(newDynamicWidth);
            setDisableInputTooltip(pickerRef.current.scrollWidth >= newDynamicWidth);
        }, []);
        useLayoutEffect(() => {
            setWidth();
        }, [storePickerStore, setWidth]);

        return (
            <Stack
                direction='row'
                sx={[
                    {
                        boxShadow: '0px 3px 5px -1px #00000033, 0px 6px 10px 0px #00000024, 0px 1px 18px 0px #0000001F',
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #BCBCBC',
                        borderRadius: '4px',
                        px: '18px',
                        py: '16px',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    },
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
            >
                <Stack direction='row' spacing='20px' alignItems='center' maxWidth='calc(100vw - 375px)'>
                    <Typography whiteSpace={'nowrap'} variant='body1' flexShrink={0}>
                        {selectionCount} Selected
                    </Typography>
                    <ButtonTruncated
                        sx={{ maxWidth: '370px', flexShrink: 1 }}
                        typography={{ sx: { textTransform: 'none' } }}
                        size='large'
                        variant='contained'
                        onClick={() => {
                            dialogService.showConfirm(
                                {
                                    message: `You are about to mark ${selectionCount} selected rows as "${
                                        primaryStore ? primaryStore.name : 'NO STORE'
                                    }". Proceed?`,
                                },
                                () => onApplyActions(primaryStore, true),
                            );
                        }}
                    >
                        Mark as {primaryStore ? primaryStore.name : 'NO STORE'}
                    </ButtonTruncated>
                    <ButtonTruncated
                        sx={{ maxWidth: '370px', flexShrink: 1 }}
                        typography={{ sx: { textTransform: 'none' } }}
                        variant='contained'
                        size='large'
                        onClick={() => {
                            dialogService.showConfirm(
                                {
                                    message: `You are about to mark ${selectionCount} selected rows Not "${
                                        primaryStore ? primaryStore.name : 'NO STORE'
                                    }". Proceed?`,
                                },
                                () => onApplyActions(primaryStore, false),
                            );
                        }}
                    >
                        Mark as Not {primaryStore ? primaryStore.name : ''}
                    </ButtonTruncated>
                    <ButtonTruncated
                        sx={{ maxWidth: '370px', flexShrink: 1 }}
                        typography={{ sx: { textTransform: 'none' } }}
                        variant='contained'
                        size='large'
                        onClick={onClearMark}
                    >
                        Clear Mark
                    </ButtonTruncated>
                    <Stack
                        direction='row'
                        sx={{
                            boxShadow: '0px 2px 4px 0px #00000040',
                            borderRadius: '4px',
                            flexShrink: 1,
                            maxWidth: '50%',
                        }}
                    >
                        <Tooltip
                            open={inputMenuIsOpen ? false : undefined}
                            title={disableInputTooltip || inputMenuIsOpen ? '' : storePickerStore?.name}
                            disableHoverListener={disableInputTooltip}
                            disableTouchListener={disableInputTooltip}
                            disableFocusListener={disableInputTooltip}
                        >
                            <Box
                                ref={pickerRef}
                                sx={{
                                    minWidth: '246px',
                                    maxWidth: '370px',
                                    width: `${dynamicWidth}px`,
                                    display: 'flex',
                                    flexGrow: 1,
                                }}
                            >
                                <Typography
                                    id='input_hidden_placeholder'
                                    sx={{
                                        position: 'absolute',
                                        height: 0,
                                        overflow: 'hidden',
                                        whiteSpace: 'pre',
                                    }}
                                >
                                    {storePickerStore?.name}
                                </Typography>

                                <StorePicker
                                    placeholder='Select Store'
                                    onOpen={() => {
                                        setInputMenuIsOpen(true);
                                    }}
                                    onClose={() => {
                                        setInputMenuIsOpen(false);
                                    }}
                                    fullWidth
                                    useShortenedDisplayValueForInput
                                    componentsProps={{
                                        popper: { style: { width: 'fit-content' }, placement: 'bottom-start' },
                                    }}
                                    label=''
                                    size='small'
                                    value={storePickerStore}
                                    onChange={(event, newStore) => {
                                        setStorePickerStore(newStore);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start' sx={{ mr: 0, ml: 0.5 }}>
                                                <Icon>search</Icon>
                                            </InputAdornment>
                                        ),
                                        // eslint-disable-next-line no-null/no-null
                                        ...(!storePickerStore && { endAdornment: null }),
                                    }}
                                    sx={{
                                        '.MuiFormControl-root': { height: '100%' },
                                        '.MuiInputBase-root': {
                                            'borderTopRightRadius': 0,
                                            'borderBottomRightRadius': 0,
                                            'height': '100%',
                                            '.MuiAutocomplete-popupIndicator': {
                                                display: 'none',
                                            },
                                            '&.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                                                pr: storePickerStore ? '36px' : '6px',
                                            },
                                            '&.MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                                                pl: '5px',
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </Tooltip>
                        <ButtonTruncated
                            typography={{ sx: { textTransform: 'none' } }}
                            sx={{
                                maxWidth: '370px',
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                boxShadow: 'none',
                                flexShrink: '1',
                            }}
                            size='large'
                            variant='contained'
                            // onClick={() => {
                            //     onApplyActions(storePickerStore, true);
                            // }}
                            onClick={() => {
                                dialogService.showConfirm(
                                    {
                                        message: `You are about to mark ${selectionCount} selected rows Not "${
                                            storePickerStore ? storePickerStore.name : 'NO STORE'
                                        }". Proceed?`,
                                    },
                                    () => onApplyActions(storePickerStore, true),
                                );
                            }}
                        >
                            Mark as {storePickerStore ? storePickerStore.name : '[store]'}
                        </ButtonTruncated>
                    </Stack>
                </Stack>
                <IconButton onClick={onClear}>
                    <Icon>close</Icon>
                </IconButton>
            </Stack>
        );
    },
);

const MemoizedMlTrainingActionsBar = memo(MlTrainingActionsBar);
export { MemoizedMlTrainingActionsBar as MlTrainingActionsBar };
